import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import {
  getAnalytics,
  provideAnalytics,
  ScreenTrackingService,
  UserTrackingService,
} from '@angular/fire/analytics';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getAuth, provideAuth } from '@angular/fire/auth';
import {
  provideAppCheck,
  initializeAppCheck,
  ReCaptchaV3Provider,
} from '@angular/fire/app-check';
import { environment } from '../environments/environment';
import { provideHttpClient } from '@angular/common/http';
import { provideClientHydration } from '@angular/platform-browser';
import { routes } from './app.routes';
import { LoggerService } from './core/services/common/logger.service';
import {
  provideFunctions,
  getFunctions,
  connectFunctionsEmulator,
} from '@angular/fire/functions';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Router with various features enabled
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    // Disable Event Replay overlay due to mobile freeze issue; re-enable once Angular bug is fixed
    provideClientHydration(),

    // Firebase core and Firestore
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),

    // Firebase Auth
    provideAuth(() => getAuth()),

    // Firebase Analytics
    provideAnalytics(() => getAnalytics()),
    ScreenTrackingService,
    UserTrackingService,

    // Firebase Performance Monitoring
    providePerformance(() => getPerformance()),

    // Firebase App Check with reCAPTCHA-based validation
    provideAppCheck(() => {
      // Skip App Check initialization during SSR or in development
      if (typeof window === 'undefined') {
        // SSR environment - return minimal config
        return null as any;
      }

      if (isDevMode()) {
        // Development environment - use debug token instead of ReCaptcha
        // @ts-ignore
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        return initializeAppCheck(getApp(), {
          provider: new ReCaptchaV3Provider('debug'), // Use debug provider
          isTokenAutoRefreshEnabled: false, // Disable auto-refresh in dev
        });
      }

      // Production environment - use actual ReCaptcha
      return initializeAppCheck(getApp(), {
        provider: new ReCaptchaV3Provider(environment.recaptchaSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    }),

    // HTTP Client
    provideHttpClient(),

    // Logger Service
    LoggerService,

    // Firebase Functions (for Cloud Functions support)
    provideFunctions(() => {
      const functions = getFunctions();
      if (
        typeof window !== 'undefined' &&
        window.location.hostname === 'localhost'
      ) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      return functions;
    }),
  ],
};
